import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Theme-System (Light Default + Dark-Toggle). Setzt data-theme auf <html>,
// persistiert in localStorage (eigener Key `portal-theme`, getrennt von admin-ui).
// Theming laeuft ueber CSS-Variablen ([data-theme] in index.css), nicht ueber
// Tailwind dark:-Varianten.
type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "portal-theme";

function initialTheme(): Theme {
  const saved = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  return saved === "dark" || saved === "light" ? saved : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  function toggleTheme() {
    // Transitions kurz unterdruecken, damit der Farbwechsel nicht "haengt".
    document.documentElement.setAttribute("data-theme-switching", "");
    setTheme((t) => (t === "light" ? "dark" : "light"));
    requestAnimationFrame(() =>
      requestAnimationFrame(() => document.documentElement.removeAttribute("data-theme-switching")),
    );
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
